import railsData from 'utilities/rails-data';
import {TaxonomyNode, Service, ServiceMap} from 'sections/types';

export class Taxonomy {
  taxonomy: TaxonomyNode[];
  serviceMap: ServiceMap;

  constructor(taxonomy: TaxonomyNode[]) {
    this.taxonomy = taxonomy;
    this.serviceMap = taxonomy.reduce(serviceReducer, {});
  }

  getTaxonomyNode(serviceHandleOrSlug: string): TaxonomyNode | null {
    return this.serviceMap[serviceHandleFromSlug(serviceHandleOrSlug)] || null;
  }

  getService(serviceHandleOrSlug: string): Service | null {
    const taxonomyNode = this.getTaxonomyNode(serviceHandleOrSlug);
    return taxonomyNode && taxonomyNode.service;
  }

  getChildren(service: Service): TaxonomyNode[] {
    return this.serviceMap[service.handle].children;
  }

  getAncestors(serviceHandle: string): Service[] {
    const paths: string[] = [];

    return (findPath(paths, this.taxonomy, serviceHandle) || []).map(
      handle => this.serviceMap[handle].service
    );
  }

  getFlattenedTaxonomyNode(taxonomyNode: TaxonomyNode): TaxonomyNode {
    return [taxonomyNode].reduce(taxonomyFlattenReducer, [])[0];
  }

  isSelectable(service: Service): boolean {
    return !this.serviceMap[service.handle].children;
  }

  isCategory(serviceHandle: string): boolean {
    return this.getAncestors(serviceHandle).length === 0;
  }
}

export function serviceHandleFromSlug(slug: string) {
  return slug.replace(/-/g, '_');
}

function findPath(paths: string[], nodes: TaxonomyNode[], handle: string) {
  let ancestorPaths: string[] | null = null;

  nodes.forEach(node => {
    const {service} = node;

    if (service.handle === handle) {
      ancestorPaths = paths;
    }

    if (ancestorPaths === null && node.children && node.children.length) {
      ancestorPaths = findPath(
        [...paths, service.handle],
        node.children,
        handle
      );
    }
  });

  return ancestorPaths;
}

function filterTaxonomy(taxonomy: TaxonomyNode[], serviceHandles: string[]) {
  return filterTaxonomyByServices(
    taxonomy.reduce(taxonomyFlattenReducer, []),
    serviceHandles
  );
}

/**
 * This reducer applies to a collection of TaxonomyNodes recursively
 * and returns an object where the key is the service handle and the
 * value is the service.
 *
 * This is useful for flattening the taxonomy tree into an object where
 * you could access any service by it's handle.
 *
 */
function serviceReducer(
  services: ServiceMap,
  taxonomyNode: TaxonomyNode
): ServiceMap {
  const {service, children} = taxonomyNode;
  const childServices: ServiceMap = (children || []).length
    ? children.reduce(serviceReducer, {})
    : {};

  return {
    ...services,
    ...childServices,
    [service.handle]: {...taxonomyNode},
  };
}

/**
 * This reducer applies to a collection of taxonomy nodes and will
 * recursively flatten the children so that each node only has
 * one level of children.
 *
 */
function taxonomyFlattenReducer(
  taxonomies: TaxonomyNode[],
  taxonomyNode: TaxonomyNode
): TaxonomyNode[] {
  const {children} = taxonomyNode;
  const flattenedChildren = (children || []).reduce(childFlattenReducer, []);

  return [...taxonomies, {...taxonomyNode, children: flattenedChildren}];
}

function childFlattenReducer(
  childNodes: TaxonomyNode[],
  taxonomyNode: TaxonomyNode
): TaxonomyNode[] {
  const {children} = taxonomyNode;
  const flattenedChildren = [
    ...(children || []).reduce(childFlattenReducer, []),
  ];

  return [...childNodes, taxonomyNode, ...flattenedChildren];
}

/**
 * This function takes a collection of taxonomy nodes and filters
 * out children based on the specified serviceHandles.
 *
 * WARNING: It does NOT recursively filter children, so you should
 * first use the `taxonomyFlattenReducer` function to flatten the
 * children if you're working with a recursive structure still.
 *
 */
function filterTaxonomyByServices(
  taxonomies: TaxonomyNode[],
  serviceHandles: string[]
): TaxonomyNode[] {
  let filteredTaxonomies = taxonomies.map(taxonomy => {
    const {children} = taxonomy;

    const filteredChildren = (children || []).length
      ? filterChildByServices(children, serviceHandles)
      : [];

    return {
      ...taxonomy,
      children: filteredChildren,
    };
  });

  filteredTaxonomies = filteredTaxonomies.filter((taxonomy: TaxonomyNode) => {
    const {children} = taxonomy;
    return children && children.length;
  });

  return filteredTaxonomies;
}

function filterChildByServices(
  taxonomies: TaxonomyNode[],
  serviceHandles: string[]
): TaxonomyNode[] {
  return taxonomies.filter((taxonomy: TaxonomyNode) => {
    const {service} = taxonomy;
    return serviceHandles.includes(service.handle);
  });
}

export enum TaxonomyName {
  Base = 'base',
  Navigational = 'nav',
}

function buildTaxonomyMap() {
  return {
    base: railsData('taxonomy.services'),
    nav: railsData('nav_taxonomy.services'),
  };
}

function fetchTaxonomy(name: TaxonomyName = TaxonomyName.Base): Taxonomy {
  const taxonomyMap = buildTaxonomyMap();
  const taxonomyData = taxonomyMap[name] || taxonomyMap.base;

  return new Taxonomy(taxonomyData);
}

export {filterTaxonomy, fetchTaxonomy};
